/**
 * Material Taxonomy Detail Component
 * Detail/edit view for Material Taxonomies (Packaging PCF)
 * Reference: Special Ingredients detail pattern
 */

import { Breadcrumbs, Button, Icon, LottieLoader, Notification } from '@mondra/ui-components'
import { useFormik } from 'formik'
import React, { FC, useEffect, useState, useCallback, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'

import ActivityRightPanel from '../../../../components/ActivityRightPanel'
import SelectCreatable from '../../../../components/CreatableSelect'
import InputItem from '../../../../components/InputItem'
import { RecordHeader } from '../../../../components/RecordHeader'
import UnsavedChangesWarning from '../../../../components/UnsavedChangesWarning'
import { ActivityTypes, ROUTES } from '../../../../const'
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'
import { usePrompt } from '../../../../hooks/usePrompt'
import { getFieldAudit } from '../../../../redux/action-creators/auditActions'
import { showCorrelationIdError } from '../../../../redux/correlationIdSlice'
import {
  handlePublishStatus,
  setDefaultMaterialTaxonomy,
} from '../../../../redux/materialTaxonomySlice'
import {
  fetchMaterialTaxonomy,
  fetchMaterialTaxonomyConfiguration,
} from './materialTaxonomyActions'
import {
  formatProcessingActivityLabel,
  functionalUnitFormSchema,
  normalizeProcessingActivityId,
  transformFunctionalUnitValues,
  sortDropdown,
} from './utils'
import {
  createFunctionalUnit,
  updateFunctionalUnit,
  publishFunctionalUnit,
  unpublishFunctionalUnit,
  getClasses,
  getCategories,
  createCategory,
  updateCategory,
  publishCategory,
  unpublishCategory,
} from './api'
import { IDropdown } from './types'

import './index.scss'

const MaterialTaxonomyDetail: FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const { loading, functionalUnit, configuration } = useAppSelector(
    state => state.materialTaxonomySlice
  )

  const [showNotification, setShowNotification] = useState<boolean>(false)
  const [showError, setShowError] = useState<string>('')
  const [showPublished, setShowPublished] = useState<boolean>(false)
  const [categoryList, setCategoryList] = useState<IDropdown[]>([])
  const [packagingMaterialClassId, setPackagingMaterialClassId] = useState<number>(0)

  const functionalUnitId = searchParams.get('itemId') || ''
  const isNew = functionalUnitId === 'new'

  // Initialize data on mount
  useEffect(() => {
    if (functionalUnitId === '') {
      navigate(ROUTES.notFound)
      return
    }

    // Load Packaging Material class and categories for both new and edit mode
    getClasses().then(res => {
      // There should only be one class: "Packaging Material"
      if (res.data && res.data.length > 0) {
        const packagingMaterialClass = res.data[0]
        setPackagingMaterialClassId(packagingMaterialClass.id)

        // Pre-load categories for this class
        getCategories(packagingMaterialClass.id).then(catRes => {
          setCategoryList(catRes.data)
        })
      }
    })

    if (isNew) {
      dispatch(fetchMaterialTaxonomyConfiguration('0'))
    } else {
      dispatch(fetchMaterialTaxonomyConfiguration(functionalUnitId))
      dispatch(fetchMaterialTaxonomy(functionalUnitId, () => navigate(ROUTES.materialTaxonomies)))
    }

    return () => {
      dispatch(setDefaultMaterialTaxonomy())
    }
  }, [functionalUnitId, isNew, dispatch, navigate])

  // Normalised baseline: same shape the form is seeded with, so the dirty
  // check does not fire on defaulting alone
  const baselineValues = useMemo(
    () => ({
      ...functionalUnit,
      categoryId: functionalUnit.categoryId || 0,
      disposalIncinerationProcessingActivityId:
        functionalUnit.disposalIncinerationProcessingActivityId ?? null,
      disposalLandfillProcessingActivityId:
        functionalUnit.disposalLandfillProcessingActivityId ?? null,
      transportCondition: functionalUnit.transportCondition ?? 0,
    }),
    [functionalUnit]
  )

  // Form configuration
  const formik = useFormik({
    initialValues: baselineValues,
    enableReinitialize: true,
    validationSchema: functionalUnitFormSchema,
    onSubmit: async (_values, { setSubmitting }) => {
      // Publish/Unpublish action
      if (isNew) {
        setSubmitting(false)
        return
      }
      if (functionalUnit.state === 0) {
        try {
          await publishFunctionalUnit(functionalUnitId)
          dispatch(handlePublishStatus(1))
          setShowPublished(true)
          setTimeout(() => setShowPublished(false), 4000)
        } catch (error: any) {
          handleError(error)
        } finally {
          setSubmitting(false)
        }
      } else {
        try {
          await unpublishFunctionalUnit(functionalUnitId)
          dispatch(handlePublishStatus(0))
          setShowPublished(true)
          setTimeout(() => setShowPublished(false), 4000)
        } catch (error: any) {
          handleError(error)
        } finally {
          setSubmitting(false)
        }
      }
    },
  })

  // Error handler
  const handleError = useCallback(
    (error: any) => {
      if (error.showCorrelationId) {
        dispatch(showCorrelationIdError(error.CorrelationId))
      } else {
        const errorMessage =
          typeof error.response?.data === 'string'
            ? error.response.data
            : typeof error.response?.data?.errors === 'string'
            ? error.response.data.errors
            : Object.values(error.response?.data?.errors || {})
                .flat()
                .join('\n') ||
              error.message ||
              'Something went wrong.'

        setShowError(errorMessage)
        setTimeout(() => setShowError(''), 5000)
      }
    },
    [dispatch]
  )

  // Save draft handler
  const saveDraft = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (formik.isSubmitting) return

      formik.setSubmitting(true)
      try {
        const validationErrors = await formik.validateForm()
        if (Object.keys(validationErrors).length > 0) {
          await formik.setTouched(
            Object.fromEntries(Object.keys(validationErrors).map(key => [key, true])),
            false
          )
          setShowError('Please correct the highlighted fields before saving.')
          return
        }
        const transformedValues = transformFunctionalUnitValues(formik.values)
        if (isNew) {
          await createFunctionalUnit(transformedValues)
          navigate(ROUTES.materialTaxonomies)
        } else {
          await updateFunctionalUnit(functionalUnitId, transformedValues)
          await dispatch(
            fetchMaterialTaxonomy(functionalUnitId, () => navigate(ROUTES.materialTaxonomies))
          )
          setShowNotification(true)
          setTimeout(() => setShowNotification(false), 5000)
        }
      } catch (error: any) {
        handleError(error)
      } finally {
        formik.setSubmitting(false)
      }
    },
    [formik, isNew, functionalUnitId, dispatch, navigate, handleError]
  )

  // Handle category creation
  const handleCreateCategory = useCallback(
    async (newValue: string) => {
      if (packagingMaterialClassId > 0) {
        const newCategory = await createCategory({
          name: newValue,
          classId: packagingMaterialClassId,
        })
        const res = await getCategories(packagingMaterialClassId)
        setCategoryList(res.data)
        formik.setFieldValue('categoryId', newCategory.data.id)
      }
    },
    [packagingMaterialClassId, formik]
  )

  // Handle category publish/unpublish
  const handleCategoryPublish = useCallback(async () => {
    if (configuration.path?.categoryId && configuration.path.categoryId > 0) {
      await publishCategory(configuration.path.categoryId.toString())
      dispatch(fetchMaterialTaxonomyConfiguration(functionalUnitId))
    }
  }, [configuration.path?.categoryId, functionalUnitId, dispatch])

  const handleCategoryUnpublish = useCallback(async () => {
    if (configuration.path?.categoryId && configuration.path.categoryId > 0) {
      await unpublishCategory(configuration.path.categoryId.toString())
      dispatch(fetchMaterialTaxonomyConfiguration(functionalUnitId))
    }
  }, [configuration.path?.categoryId, functionalUnitId, dispatch])

  // Handle category edit
  const handleCategoryEdit = useCallback(
    async (name: string | number, _code: string | null) => {
      if (
        configuration.path?.categoryId &&
        configuration.path.categoryId > 0 &&
        configuration.path.classId
      ) {
        await updateCategory(configuration.path.categoryId.toString(), {
          name: typeof name === 'string' ? name : String(name),
          classId: configuration.path.classId,
        })
        dispatch(fetchMaterialTaxonomyConfiguration(functionalUnitId))
      }
    },
    [configuration.path?.categoryId, configuration.path?.classId, functionalUnitId, dispatch]
  )

  const processingActivityOptions = useMemo(
    () =>
      configuration.processingActivities.map(pa => ({
        label: formatProcessingActivityLabel(pa),
        value: pa.id,
      })),
    [configuration.processingActivities]
  )

  const { setFieldValue } = formik
  const handleIncinerationActivityChange = useCallback(
    (e: React.ChangeEvent<Element>) => {
      setFieldValue(
        'disposalIncinerationProcessingActivityId',
        normalizeProcessingActivityId((e.target as HTMLSelectElement).value)
      )
    },
    [setFieldValue]
  )
  const handleLandfillActivityChange = useCallback(
    (e: React.ChangeEvent<Element>) => {
      setFieldValue(
        'disposalLandfillProcessingActivityId',
        normalizeProcessingActivityId((e.target as HTMLSelectElement).value)
      )
    },
    [setFieldValue]
  )
  const handleCancel = useCallback(() => navigate(ROUTES.materialTaxonomies), [navigate])
  const handleClassAudit = useCallback(
    () =>
      dispatch(
        getFieldAudit('Taxonomy/PackagingMaterials', 'class', configuration.path?.classId || 0)
      ),
    [configuration.path?.classId, dispatch]
  )
  const handleCategoryAudit = useCallback(
    () =>
      dispatch(
        getFieldAudit(
          'Taxonomy/PackagingMaterials',
          'category',
          configuration.path?.categoryId || 0
        )
      ),
    [configuration.path?.categoryId, dispatch]
  )
  const isDirty = formik.dirty

  // Configuration is deliberately excluded from the form baseline: late options never clear IDs.
  usePrompt('There are unsaved changes, do you want to leave?', !isNew && isDirty)

  if (loading && !isNew) {
    return (
      <div className="overlay">
        <LottieLoader
          lottieType="butterflyLottie"
          style={{ height: '10.25rem', width: '10.25rem' }}
        />
      </div>
    )
  }

  return (
    <>
      <div className="piece content material-taxonomy-detail pb-20">
        {/* Notifications */}
        {showError && (
          <div className="piece-notification z-dialog">
            <Notification type="error" label="Error:" description={showError} />
          </div>
        )}
        {showPublished && (
          <div className="piece-notification z-dialog">
            <Notification
              type="success"
              label="Success:"
              description="Status was updated!"
              isToast={false}
            />
          </div>
        )}
        {showNotification && (
          <div className="piece-notification z-dialog">
            <Notification
              type="success"
              label="Success:"
              description="Item was updated!"
              isToast={false}
            />
          </div>
        )}

        {/* Breadcrumbs */}
        <div className="mb-3 mt-3">
          <Breadcrumbs
            routes={[
              {
                path: ROUTES.materialTaxonomies,
                title: 'Material Taxonomies',
              },
              {
                path: `${ROUTES.materialTaxonomyDetail}?itemId=${functionalUnitId}`,
                title: isNew ? 'New Material Taxonomy' : functionalUnit.name || 'Material Taxonomy',
              },
            ]}
          />
        </div>

        {/* Status Header */}
        {!isNew && (
          <RecordHeader
            label={functionalUnit.code}
            isPublished={functionalUnit.state === 1}
            datePublished={functionalUnit.lastPublishedDate}
          />
        )}

        {/* Instructions */}
        <div>
          <h5 className="mb-5 mt-5">Indicators information</h5>
          <ul>
            <li className="mb-5 flex">
              <Icon type="warning" className="mr-3 text-rose-700" /> - this indicator shows you that
              field value is not published. Press it for publishing field value (only for Managers)
            </li>
            <li className="mb-5 flex">
              <Icon type="checkmarkOutline" className="mr-3 text-emerald-700" /> - this indicator
              shows you that field value is published. Press it for unpublishing field value (only
              for Managers)
            </li>
            {!isNew && (
              <li className="flex">
                <Icon type="informationSquare" className="mr-3 text-green-200" /> - press this
                button if you want to show audit information of the field
              </li>
            )}
          </ul>
        </div>

        {/* Form */}
        <form onSubmit={formik.handleSubmit} className="piece-content">
          {/* Material Taxonomy Section */}
          <h5 className="mt-5">Material Taxonomy</h5>
          {isNew ? (
            <div className="piece-content-inputs">
              <InputItem
                name="Class"
                description=""
                type="input"
                defaultValue="Packaging Material"
                disabled
                formikName="class"
                formik={formik}
              />
              <SelectCreatable
                name="Category"
                create={true}
                formik={formik}
                showAsterisk
                disabled={categoryList.length === 0}
                formikName="categoryId"
                onCreateOption={handleCreateCategory}
                defaultValue={formik.values.categoryId}
                options={categoryList.map(el => ({
                  label: el.name,
                  value: el.id,
                }))}
              />
              <InputItem
                name="Material name"
                description=""
                type="input"
                disabled={formik.values.categoryId === 0}
                formik={formik}
                showAsterisk
                onChange={formik.handleChange}
                defaultValue={formik.values.name}
                formikName="name"
              />
            </div>
          ) : (
            <div className="piece-content-inputs">
              <InputItem
                name="Class"
                description=""
                type="input"
                formik={formik}
                showAsterisk
                onChange={formik.handleChange}
                disabled={true}
                auditHistory={true}
                defaultValue={configuration.path?.class || ''}
                formikName="class"
                onGetAuditClick={handleClassAudit}
              />
              <InputItem
                name="Category"
                description=""
                type="input"
                formik={formik}
                showAsterisk
                onChange={formik.handleChange}
                disabled={true}
                defaultValue={configuration.path?.category || ''}
                formikName="category"
                unpublished={configuration.path?.categoryState}
                edit={false}
                auditHistory={true}
                code={configuration.path?.categoryCode}
                onPublishClick={handleCategoryPublish}
                onUnpublishClick={handleCategoryUnpublish}
                onEditClick={handleCategoryEdit}
                onGetAuditClick={handleCategoryAudit}
              />
              <InputItem
                name="Material name"
                description=""
                type="input"
                formik={formik}
                showAsterisk
                onChange={formik.handleChange}
                defaultValue={formik.values.name}
                formikName="name"
              />
            </div>
          )}

          {/* Unit Info Section */}
          <h5 className="mt-5">Unit info</h5>
          <div className="piece-content-inputs">
            <InputItem
              name="External code"
              description=""
              type="input"
              disabled
              formik={formik}
              onChange={formik.handleChange}
              defaultValue={formik.values.code || ''}
              formikName="code"
            />
            <InputItem
              name="Material name Aliases"
              description=""
              type="input"
              formik={formik}
              onChange={formik.handleChange}
              defaultValue={formik.values.alias || ''}
              formikName="alias"
            />
          </div>

          {/* Transport and Storage Section */}
          <h5 className="mt-5">Transport and Storage</h5>
          <div className="piece-content-inputs">
            <InputItem
              name="Storage/Transport condition"
              description=""
              type="select"
              formik={formik}
              defaultValue={formik.values.transportCondition ?? 0}
              onChange={formik.handleChange}
              formikName="transportCondition"
              options={sortDropdown(configuration.transportCondition || [])}
            />
          </div>

          <h5 className="mt-5">Disposal processing activities</h5>
          <div className="piece-content-inputs">
            <InputItem
              name="Incineration processing activity"
              description="The published packaging processing activity used for incineration."
              type="select"
              formik={formik}
              onChange={handleIncinerationActivityChange}
              formikName="disposalIncinerationProcessingActivityId"
              defaultValue={formik.values.disposalIncinerationProcessingActivityId ?? -1}
              options={processingActivityOptions}
            />
            <InputItem
              name="Landfill processing activity"
              description="The published packaging processing activity used for landfill."
              type="select"
              formik={formik}
              onChange={handleLandfillActivityChange}
              formikName="disposalLandfillProcessingActivityId"
              defaultValue={formik.values.disposalLandfillProcessingActivityId ?? -1}
              options={processingActivityOptions}
            />
          </div>

          {/* Action Buttons */}
          <div className="material-taxonomy-detail__actions">
            <div className="flex w-full flex-col items-center gap-4">
              <UnsavedChangesWarning isDirty={isDirty} />
              <div className="flex flex-wrap justify-center gap-4">
                <Button type="button" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button type="button" onClick={saveDraft} disabled={formik.isSubmitting}>
                  Save Draft
                </Button>
                {!isNew && (
                  <Button type="submit" disabled={functionalUnit.state === 1}>
                    {functionalUnit.state === 0 ? 'Publish' : 'Published'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
      <ActivityRightPanel
        activityId={functionalUnitId}
        activityType={ActivityTypes.MaterialTaxonomy}
        auditCreatedBy={functionalUnit.researcherName || ''}
        auditLastUpdatedBy={functionalUnit.lastUpdatedName}
        isVerified={formik.values.isVerified}
        verificationComments={formik.values.verificationComments}
        verifiedByName={formik.values.verifiedByName}
        verifiedOn={formik.values.verifiedOn}
        onChange={formik.handleChange}
        hideCompanyselect
      />
    </>
  )
}

export default MaterialTaxonomyDetail
